/**
 * The Sentry tunnel route, shared by the browser client and the Worker.
 *
 * `next.config.js` sets `tunnelRoute: '/monitoring'`, and `@sentry/nextjs`
 * turned that into `options.tunnel = '/monitoring?o=<org>&p=<project>&r=<region>'`
 * inside its client init (`client/tunnelRoute.js`, `applyTunnelRouteOption`),
 * so browser events leave same-origin and survive ad blockers. Nothing on the
 * Vite side does that automatically, so the same derivation lives here: the
 * browser builds the path from the DSN it initialises with, and the Worker
 * validates an incoming envelope against the DSN it is configured with. Both
 * read one parser, so the two halves cannot drift.
 *
 * Framework-free on purpose — it is imported from `src/spa/sentry.ts` (browser)
 * and `src/worker/monitoring.ts` (workerd).
 */

/** The path `src/worker/monitoring.ts` serves; the value `tunnelRoute` had under Next. */
export const SENTRY_TUNNEL_ROUTE = '/monitoring'

export interface SentryDsnComponents {
  /** `o<org>.ingest.<region>.sentry.io` for SaaS; the bare host otherwise. */
  host: string
  projectId: string
  /** Non-null only for a Sentry SaaS DSN. */
  orgId: string | null
  /** Non-null only for a regional Sentry SaaS DSN. */
  regionCode: string | null
}

// The same expression @sentry/nextjs matches the host against before deciding
// a DSN can be tunnelled at all.
const SAAS_HOST_PATTERN = /^o(\d+)\.ingest(?:\.([a-z]{2}))?\.sentry\.io$/

export function parseSentryDsn(dsn: string): SentryDsnComponents | null {
  if (!dsn) return null

  let url: URL
  try {
    url = new URL(dsn)
  } catch {
    return null
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
  // The public key is the DSN's user info; a URL without it is not a DSN.
  if (!url.username) return null

  const projectId = url.pathname.split('/').filter(Boolean).pop()
  if (!projectId) return null

  const saas = SAAS_HOST_PATTERN.exec(url.host)

  return {
    host: url.host,
    projectId,
    orgId: saas?.[1] ?? null,
    regionCode: saas?.[2] ?? null,
  }
}

/**
 * The `tunnel` option value, or `undefined` when the DSN is not a Sentry SaaS
 * DSN — which is exactly when @sentry/nextjs logged "Will not tunnel events"
 * and left the option unset, so events go straight to ingest.
 */
export function sentryTunnelPath(dsn: string, tunnelRoute: string = SENTRY_TUNNEL_ROUTE): string | undefined {
  const components = parseSentryDsn(dsn)
  if (!components?.orgId) return undefined

  const path = `${tunnelRoute}?o=${components.orgId}&p=${components.projectId}`
  return components.regionCode ? `${path}&r=${components.regionCode}` : path
}
